import { Action, Dispatch } from 'redux';
import { isRacker } from '@rss-engineering/fabric-ui-session';
import { NAMESPACE_PREFERENCES } from 'appConstants';

import {
  setPortalPreference,
  PortalPreferenceAction,
  CreateTicketDraft,
} from 'reducers';

import { FeatureFlags } from 'services';

import moment from 'moment';
import { UpdateTicketDraft } from 'types';

enum ResumeTicketAction {
  CATEGORY = 'category',
  ACCOUNT_ID = 'accountId',
  PRODUCT = 'subcategory',
  SEVERITY = 'severity',
  RESOURCES = 'resources',
  BPIT_TEMPLATE_ID = 'bpitTemplateId',
  SUBJECT = 'subject',
  COMMENT = 'comment',
  RECIPIENTS = 'recipients',
  SELECTED_STEP = 'selectedStep',
  OPEN_RESUME_TICKET_MODAL = 'OPEN_RESUME_TICKET_MODAL',
  CLOSE_RESUME_TICKET_MODAL = 'CLOSE_RESUME_TICKET_MODAL',
  FORM_DATA = 'formData',
  VALIDATION_START = 'VALIDATION_START',
  VALIDATION_END = 'VALIDATION_END',
}

export interface ResumeTicketModalAction extends Action {
  readonly isResumeTicketModalOpen?: boolean;
  readonly isValidating?: boolean;
}

export interface ResumeTicket {
  readonly isResumeTicketModalOpen: boolean;
  readonly isValidating: boolean;
}

const initialState = {
  isResumeTicketModalOpen: false,
  isValidating: false,
} as ResumeTicket;

/* To open resume ticket modal */
const openResumeTicketModal = (): ResumeTicketModalAction => ({
  type: ResumeTicketAction.OPEN_RESUME_TICKET_MODAL,
});

/* To close resume ticket modal */
const closeResumeTicketModal = (): ResumeTicketModalAction => ({
  type: ResumeTicketAction.CLOSE_RESUME_TICKET_MODAL,
});

/* To indicate start of validation */
const ticketDraftValidationStart = (): ResumeTicketModalAction => ({
  type: ResumeTicketAction.VALIDATION_START,
});

/* To indicate end of validation */
const ticketDraftValidationEnd = (): ResumeTicketModalAction => ({
  type: ResumeTicketAction.VALIDATION_END,
});

const resumeTicketModalReducer = (
  state = initialState,
  action: ResumeTicketModalAction,
) => {
  switch (action.type) {
    case ResumeTicketAction.OPEN_RESUME_TICKET_MODAL:
      return {
        ...state,
        isResumeTicketModalOpen: true,
      };
    case ResumeTicketAction.CLOSE_RESUME_TICKET_MODAL:
      return {
        ...state,
        isResumeTicketModalOpen: false,
      };
    case ResumeTicketAction.VALIDATION_START:
      return {
        ...state,
        isValidating: true,
      };
    case ResumeTicketAction.VALIDATION_END:
      return {
        ...state,
        isValidating: false,
      };

    default:
      return state;
  }
};

/* Thunk */

const setCreateTicketDraftForBpit = (
  createTicketDraftKey: string,
  formData: object,
) => async (dispatch: Dispatch<PortalPreferenceAction>) => {
  let updatedCreateTicketDraft = {};

  updatedCreateTicketDraft[createTicketDraftKey] = formData;

  if (
    !isRacker() &&
    FeatureFlags.isActive('resume-ticket') &&
    Object.keys(updatedCreateTicketDraft).length > 0
  ) {
    const timeStamp = moment().toISOString();

    dispatch(
      setPortalPreference(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT, {
        ...updatedCreateTicketDraft,
        timeStamp: timeStamp,
      }),
    );
  }
};

const deleteCreateTicketDraftValue = (
  createTicketDraftKeys: string[],
) => async (dispatch: Dispatch<PortalPreferenceAction>, getState: Function) => {
  if (!isRacker() && FeatureFlags.isActive('resume-ticket')) {
    let isDraftChanged: boolean = false;
    const createTicketDraftList = getState().portalPreferences
      .createTicketDraft;
    if (createTicketDraftList) {
      createTicketDraftKeys.forEach((createTicketDraftKey) => {
        if (createTicketDraftList[createTicketDraftKey]) {
          delete createTicketDraftList[createTicketDraftKey];
          isDraftChanged = true;
        }
      });
      if (isDraftChanged) {
        await dispatch(
          setPortalPreference(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT, {
            ...createTicketDraftList,
          }),
        );
      }
    }
  }
};

const setCreateTicketDraft = (createTicketKeys: string[]) => async (
  dispatch: Dispatch<PortalPreferenceAction>,
  getState: Function,
) => {
  let updatedCreateTicketDraft = {};
  const createTicketDraft =
    getState().portalPreferences.createTicketDraft || ({} as CreateTicketDraft);

  /**
   * Update draft only if existing draft values and latest draft values
   * doesn't match.
   */

  createTicketKeys.forEach((key: string) => {
    const draftValue =
      getState().newTicket.payload[key] || getState().newTicket[key];
    if (createTicketDraft[key] !== draftValue) {
      updatedCreateTicketDraft[key] = draftValue;
    }
  });

  const timeStamp = moment().toISOString();
  if (
    !isRacker() &&
    FeatureFlags.isActive('resume-ticket') &&
    Object.keys(updatedCreateTicketDraft).length > 0
  ) {
    await dispatch(
      setPortalPreference(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT, {
        ...updatedCreateTicketDraft,
        timeStamp: timeStamp,
      }),
    );
  }
};

const setUpdateTicketDraft = (ticketId: string) => async (
  dispatch: Dispatch<PortalPreferenceAction>,
  getState: Function,
) => {
  if (FeatureFlags.isActive('resume-update-ticket') && !isRacker()) {
    const message = getState().newComment.message;
    const timeStamp = moment().toISOString();
    dispatch(
      setPortalPreference(NAMESPACE_PREFERENCES.UPDATE_TICKET_DRAFT, {
        [ticketId]: {
          message,
          timeStamp,
        },
      }),
    );
  }
};

const deleteUpdateTicketDraft = (ticketId: string) => async (
  dispatch: Dispatch<PortalPreferenceAction>,
  getState: Function,
) => {
  if (FeatureFlags.isActive('resume-update-ticket') && !isRacker()) {
    const updateTicketDraftList: UpdateTicketDraft = getState()
      .portalPreferences.updateTicketDraft;
    if (updateTicketDraftList[ticketId]) {
      delete updateTicketDraftList[ticketId];
      dispatch(
        setPortalPreference(NAMESPACE_PREFERENCES.UPDATE_TICKET_DRAFT, {
          ...updateTicketDraftList,
        }),
      );
    }
  }
};

export {
  closeResumeTicketModal,
  openResumeTicketModal,
  ResumeTicketAction,
  resumeTicketModalReducer,
  setCreateTicketDraft,
  setCreateTicketDraftForBpit,
  deleteCreateTicketDraftValue,
  setUpdateTicketDraft,
  ticketDraftValidationStart,
  ticketDraftValidationEnd,
  deleteUpdateTicketDraft,
};
